import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Pause,
  Play,
  Plus,
  Repeat,
  Trash2,
  Zap,
  ArrowLeft,
} from "lucide-react";

import { pageTransition, formatDate, formatCurrency } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  listRecurringInvoices,
  createRecurringInvoice,
  pauseRecurringInvoice,
  resumeRecurringInvoice,
  cancelRecurringInvoice,
  runDueRecurringInvoices,
} from "@/api/recurringInvoices";
import { listCurrencies, type CurrencyInfo } from "@/api/currencies";
import type { LineItem } from "@/types/invoice";
import {
  CADENCE_LABELS,
  RECURRING_STATUS_LABELS,
  type RecurringCadence,
  type RecurringInvoice,
  type RecurringStatus,
} from "@/types/recurringInvoice";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";

function statusVariant(s: RecurringStatus) {
  switch (s) {
    case "ACTIVE":
      return "success" as const;
    case "PAUSED":
      return "warning" as const;
    case "CANCELLED":
      return "danger" as const;
    case "COMPLETED":
      return "default" as const;
  }
}

export function RecurringInvoicesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { addToast } = useToast();
  const canManage = can("manage_invoices");

  const [items, setItems] = useState<RecurringInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"" | RecurringStatus>("");
  const [isBusy, setIsBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);

  // Create form state
  const [empId, setEmpId] = useState("");
  const [title, setTitle] = useState("");
  const [cadence, setCadence] = useState<RecurringCadence>("MONTHLY");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [maxOccurrences, setMaxOccurrences] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [taxRate, setTaxRate] = useState("0");
  const [dueDays, setDueDays] = useState("14");
  const [notes, setNotes] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [sendToEmail, setSendToEmail] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: "", hours: 0, rate: 0, amount: 0 },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { items } = await listRecurringInvoices(statusFilter || undefined);
      setItems(items);
    } catch {
      addToast("error", "Failed to load recurring templates");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    listCurrencies()
      .then((d) => setCurrencies(d.currencies))
      .catch(() => {
        /* optional */
      });
  }, []);

  const handlePause = async (tpl: RecurringInvoice) => {
    try {
      await pauseRecurringInvoice(tpl.id);
      addToast("success", "Template paused");
      fetchData();
    } catch {
      addToast("error", "Failed to pause template");
    }
  };

  const handleResume = async (tpl: RecurringInvoice) => {
    try {
      await resumeRecurringInvoice(tpl.id);
      addToast("success", "Template resumed");
      fetchData();
    } catch {
      addToast("error", "Failed to resume template");
    }
  };

  const handleCancel = async (tpl: RecurringInvoice) => {
    if (!confirm(`Cancel "${tpl.title}"? This stops all future generation.`)) {
      return;
    }
    try {
      await cancelRecurringInvoice(tpl.id);
      addToast("success", "Template cancelled");
      fetchData();
    } catch {
      addToast("error", "Failed to cancel template");
    }
  };

  const handleRunDue = async () => {
    setIsBusy(true);
    try {
      const result = await runDueRecurringInvoices();
      const n = result.generated.length;
      addToast(
        "success",
        n === 0
          ? "No templates were due today."
          : `Generated ${n} invoice${n === 1 ? "" : "s"}.`,
      );
      fetchData();
    } catch {
      addToast("error", "Failed to run due templates");
    } finally {
      setIsBusy(false);
    }
  };

  const updateLineItem = (
    idx: number,
    field: keyof LineItem,
    value: string | number,
  ) => {
    setLineItems((prev) => {
      const next = [...prev];
      const item = { ...next[idx], [field]: value };
      if (field === "hours" || field === "rate") {
        item.amount = Math.round((item.hours || 0) * item.rate * 100) / 100;
      }
      next[idx] = item;
      return next;
    });
  };

  const addLineItem = () =>
    setLineItems((prev) => [
      ...prev,
      { description: "", hours: 0, rate: 0, amount: 0 },
    ]);

  const removeLineItem = (idx: number) =>
    setLineItems((prev) => prev.filter((_, i) => i !== idx));

  const resetForm = () => {
    setEmpId("");
    setTitle("");
    setCadence("MONTHLY");
    setStartDate("");
    setEndDate("");
    setMaxOccurrences("");
    setCurrency("USD");
    setTaxRate("0");
    setDueDays("14");
    setNotes("");
    setAutoSend(false);
    setSendToEmail("");
    setLineItems([{ description: "", hours: 0, rate: 0, amount: 0 }]);
  };

  const handleCreate = async () => {
    if (!empId || !title || !startDate || lineItems.length === 0) {
      addToast("error", "Employee, title, start date, and line items are required");
      return;
    }
    setIsSubmitting(true);
    try {
      await createRecurringInvoice({
        employee_id: empId,
        title,
        cadence,
        start_date: startDate,
        end_date: endDate || undefined,
        max_occurrences: maxOccurrences ? parseInt(maxOccurrences, 10) : undefined,
        currency,
        tax_rate: parseFloat(taxRate) || 0,
        due_days_after_issue: parseInt(dueDays, 10) || 14,
        notes: notes || undefined,
        auto_send: autoSend,
        send_to_email: sendToEmail || undefined,
        line_items: lineItems,
      });
      addToast("success", "Recurring template created");
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err: unknown) {
      const msg =
        typeof err === "object" && err && "response" in err
          ? // @ts-expect-error - dynamic axios shape
            err.response?.data?.detail
          : undefined;
      addToast("error", typeof msg === "string" ? msg : "Failed to create template");
    } finally {
      setIsSubmitting(false);
    }
  };

  const subtotal = lineItems.reduce((s, li) => s + (li.amount || 0), 0);
  const taxAmount = (subtotal * (parseFloat(taxRate) || 0)) / 100;
  const total = subtotal + taxAmount;

  const columns: Column<RecurringInvoice>[] = [
    {
      key: "title",
      header: "Template",
      render: (t) => (
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <Repeat className="h-4 w-4 text-accent" />
          </div>
          <div>
            <p className="font-medium text-text-primary">{t.title}</p>
            <p className="text-xs text-text-muted">
              {t.employee_name || "—"} • {CADENCE_LABELS[t.cadence]}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "next_run",
      header: "Next run",
      render: (t) => (
        <span className="text-sm text-text-primary">
          {t.status === "ACTIVE" ? formatDate(t.next_run_at) : "—"}
        </span>
      ),
    },
    {
      key: "count",
      header: "Generated",
      render: (t) => (
        <span className="text-sm text-text-muted">
          {t.occurrences_generated}
          {t.max_occurrences ? ` / ${t.max_occurrences}` : ""}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Est. total",
      render: (t) => {
        const sub = (t.line_items_json || []).reduce(
          (s, li) => s + (li.amount || 0),
          0,
        );
        const tot = sub + (sub * (t.tax_rate || 0)) / 100;
        return (
          <span className="text-sm font-medium text-text-primary">
            {formatCurrency(tot, t.currency)}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      render: (t) => (
        <Badge variant={statusVariant(t.status)}>
          {RECURRING_STATUS_LABELS[t.status]}
          {t.auto_send && t.status === "ACTIVE" ? " • auto-send" : ""}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <div className="flex justify-end gap-1">
          {canManage && t.status === "ACTIVE" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePause(t);
              }}
              className="p-1.5 rounded text-warning hover:bg-warning/10"
              title="Pause"
            >
              <Pause className="h-4 w-4" />
            </button>
          )}
          {canManage && t.status === "PAUSED" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleResume(t);
              }}
              className="p-1.5 rounded text-success hover:bg-success/10"
              title="Resume"
            >
              <Play className="h-4 w-4" />
            </button>
          )}
          {canManage &&
            (t.status === "ACTIVE" || t.status === "PAUSED") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(t);
                }}
                className="p-1.5 rounded text-danger hover:bg-danger/10"
                title="Cancel"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All statuses" },
    ...Object.entries(RECURRING_STATUS_LABELS).map(([v, l]) => ({
      value: v,
      label: l,
    })),
  ];

  const cadenceOptions = Object.entries(CADENCE_LABELS).map(([v, l]) => ({
    value: v,
    label: l,
  }));

  return (
    <motion.div {...pageTransition}>
      <button
        onClick={() => navigate("/invoices")}
        className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to invoices
      </button>

      <PageHeader
        title="Recurring invoices"
        subtitle={`${items.length} template${items.length === 1 ? "" : "s"}`}
        actions={
          canManage ? (
            <>
              <Button variant="secondary" onClick={handleRunDue} isLoading={isBusy}>
                <Zap className="h-4 w-4" />
                Run due
              </Button>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" />
                New template
              </Button>
            </>
          ) : undefined
        }
      />

      <div className="flex gap-3 mb-6">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | RecurringStatus)}
          options={statusOptions}
        />
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={items}
          keyExtractor={(t) => t.id}
          isLoading={isLoading}
          emptyState={
            statusFilter ? (
              <EmptyState
                icon={Repeat}
                title="No templates match this filter"
                description="Try a different status, or clear the filter to see every template."
                action={{
                  label: "Clear filter",
                  onClick: () => setStatusFilter(""),
                }}
              />
            ) : (
              <EmptyState
                icon={Repeat}
                title="No recurring templates yet"
                description="Templates auto-generate invoices on a schedule — perfect for retainers, monthly contractors, or subscription billing."
                action={{
                  label: "New Template",
                  onClick: () => setShowCreate(true),
                }}
              />
            )
          }
        />
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false);
          resetForm();
        }}
        title="New recurring template"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Employee ID"
              value={empId}
              onChange={(e) => setEmpId(e.target.value)}
              placeholder="Employee UUID"
            />
            <Input
              label="Template title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Monthly retainer"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Cadence"
              value={cadence}
              onChange={(e) => setCadence(e.target.value as RecurringCadence)}
              options={cadenceOptions}
            />
            <Input
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="End date (optional)"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Max occurrences"
              type="number"
              value={maxOccurrences}
              onChange={(e) => setMaxOccurrences(e.target.value)}
              placeholder="unlimited"
            />
            <Input
              label="Due days after issue"
              type="number"
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
            />
            <Select
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              options={
                currencies.length
                  ? currencies.map((c) => ({
                      value: c.code,
                      label: `${c.code} — ${c.name}`,
                    }))
                  : [{ value: "USD", label: "USD — US Dollar" }]
              }
            />
          </div>

          {/* Line items */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Line items
            </label>
            <div className="space-y-2">
              {lineItems.map((li, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <Input
                    placeholder="Description"
                    value={li.description}
                    onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Hours"
                    type="number"
                    value={li.hours?.toString() || ""}
                    onChange={(e) =>
                      updateLineItem(idx, "hours", parseFloat(e.target.value) || 0)
                    }
                    className="w-20"
                  />
                  <Input
                    placeholder="Rate"
                    type="number"
                    value={li.rate?.toString() || ""}
                    onChange={(e) =>
                      updateLineItem(idx, "rate", parseFloat(e.target.value) || 0)
                    }
                    className="w-24"
                  />
                  <span className="text-sm font-medium text-text-primary w-24 text-right pb-2">
                    {formatCurrency(li.amount, currency)}
                  </span>
                  {lineItems.length > 1 && (
                    <button
                      onClick={() => removeLineItem(idx)}
                      className="p-1 text-text-muted hover:text-danger mb-2"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addLineItem}
              className="mt-2 text-xs text-accent hover:text-accent-600"
            >
              + Add line item
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Tax rate (%)"
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
            />
            <div className="flex flex-col justify-end">
              <div className="text-right space-y-1">
                <p className="text-xs text-text-muted">
                  Subtotal: {formatCurrency(subtotal, currency)}
                </p>
                {parseFloat(taxRate) > 0 && (
                  <p className="text-xs text-text-muted">
                    Tax: {formatCurrency(taxAmount, currency)}
                  </p>
                )}
                <p className="text-sm font-bold text-text-primary">
                  Per-invoice total: {formatCurrency(total, currency)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="autoSend"
              type="checkbox"
              checked={autoSend}
              onChange={(e) => setAutoSend(e.target.checked)}
              className="h-4 w-4 rounded border-border text-accent focus:ring-accent/50"
            />
            <label htmlFor="autoSend" className="text-sm text-text-primary">
              Auto-send generated invoices
            </label>
          </div>
          {autoSend && (
            <Input
              label="Send to email (optional)"
              type="email"
              value={sendToEmail}
              onChange={(e) => setSendToEmail(e.target.value)}
              placeholder="Leave blank to use employee's work email"
            />
          )}

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreate(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={isSubmitting}>
              Create template
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
